import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
// utils
import axios from '../../utils/axios';
import { AlarmDefinition, Building, DeviceTag, DeviceType, User } from '../../@types/model/user';
import { AlarmStateChange } from '../../@types/model/device';
import dayjs from 'dayjs';
import { DeliveryConfiguration } from '../../@types/model/deliveryConfiguration';

// ----------------------------------------------------------------------

type UserState = {
  loaded: boolean;
  isLoading: boolean;
  error: boolean;
  user: null | User;
  deviceTags: DeviceTag[];
  buildings: Building[];
  alarmDefinitions: AlarmDefinition[];
  deviceTypes: DeviceType[];
  alarmStateChanges: AlarmStateChange[];
  deliveryConfigurations: DeliveryConfiguration[];
  alarm_state_change_count: number;
};

const initialState: UserState = {
  isLoading: false,
  loaded: false,
  error: false,
  user: null,
  deviceTags: [],
  buildings: [],
  alarmDefinitions: [],
  deviceTypes: [],
  alarmStateChanges: [],
  deliveryConfigurations: [],
  alarm_state_change_count: 0
};

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // STOP LOADING
    stopLoading(state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.loaded = false;
      state.error = action.payload;
    },

    // GET PROFILE
    getProfileSuccess(state, action) {
      state.isLoading = false;
      state.loaded = true;
      state.user = action.payload.data as User;
      state.deviceTags = action.payload.included
        .filter((item: any) => item.type === 'App\\Domain\\Model\\DeviceTag\\DeviceTag')
        .map((item: any) => {
          let itemRefactored = item;
          if (itemRefactored.attributes.original_tag_guid)
            itemRefactored.guid = itemRefactored.attributes.original_tag_guid;
          return itemRefactored as DeviceTag;
        });
      state.alarmDefinitions = action.payload.included
        .filter((item: any) => item.type === 'App\\Domain\\Model\\User\\AlarmDefinition')
        .map((item: any) => {
          return item as AlarmDefinition;
        });

      state.deviceTypes = action.payload.included
        .filter(
          (item: any) => item.type === 'App\\Domain\\Presets\\DeviceType\\DeviceTypeInterface'
        )
        .map((item: any) => {
          return item as DeviceType;
        });

      state.deliveryConfigurations = action.payload.included
        .filter((item: any) => item.type === 'App\\Order\\Domain\\Model\\DeliveryConfiguration')
        .map((item: any) => {
          return item.attributes as DeliveryConfiguration;
        });

      state.alarmStateChanges = action.payload.included
        .filter(
          (item: any) => item.type === 'App\\Domain\\Model\\AlarmStateChange\\AlarmStateChange'
        )
        .map((item: any) => {
          return {
            guid: item.guid,
            attributes: { ...item.attributes, created: dayjs(item.attributes.created) }
          } as AlarmStateChange;
        });

      state.alarm_state_change_count = action.payload.meta.alarm_state_change_count;
      window.localStorage.setItem('user', JSON.stringify(state.user));
    },
    updateUser(state, action) {
      state.user = action.payload as User;
      window.localStorage.setItem('user', JSON.stringify(state.user));
    },
    addDeviceTag(state, action) {
      state.deviceTags = [...state.deviceTags, action.payload as DeviceTag];
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { getProfileSuccess } = slice.actions;

// ----------------------------------------------------------------------

export function startGlobalLoading() {
  return async () => {
    dispatch(slice.actions.startLoading());
  };
}

export function stopGlobalLoading() {
  return async () => {
    dispatch(slice.actions.stopLoading());
  };
}

export function getProfile() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/api/v2/user/me');
      dispatch(slice.actions.getProfileSuccess(response.data));
    } catch (error) {
      console.log('error', error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function addDeviceTag(deviceTag: DeviceTag) {
  return async () => {
    dispatch(slice.actions.addDeviceTag(deviceTag));
  };
}

export function updateProfile(user: User) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.put('/api/v2/user/me', user);
      dispatch(slice.actions.updateUser(user));
      dispatch(slice.actions.stopLoading());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      dispatch(slice.actions.stopLoading());
    }
  };
}
